import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosRequestConfig } from 'axios';
import { ApplicationApi } from '../openapi/atlantis';
import {
  ChecklistApi,
  ChecklistResponse,
  ApplicationApi as sherlockApplicationApi,
} from '../openapi/sherlock';
import {
  AgentControllerApi,
  AgentLicenseVerificationResponseStatusEnum,
  ApplicationControllerApi,
  ApplicationProgressResponse,
  ContentItem,
  QuestionAnswersResponse,
} from '../openapi/yenta';
import ErrorService from '../services/ErrorService.ts';
import { AppDispatch, ApplicationState } from '../types';
import Logger from '../utils/Logger';
import {
  getAtlantisConfiguration,
  getSherlockConfiguration,
  getYentaConfiguration,
} from '../utils/OpenapiConfigurationUtils';
import { showErrorToast } from './ToastNotificationSlice';

export const initialState: ApplicationState = {
  currentQuestion: undefined,
  progress: undefined,
  loanProgress: undefined,
  isCollapsed: false,
  questionAnswers: undefined,
};

const ApplicationSlice = createSlice({
  name: 'application',
  initialState,
  reducers: {
    saveCurrentQuestion(state, action: PayloadAction<ContentItem>) {
      state.currentQuestion = action.payload;
    },
    saveApplicationProgress(
      state,
      action: PayloadAction<ApplicationProgressResponse>,
    ) {
      state.progress = action.payload;
    },
    saveLoanProgress(state, action: PayloadAction<ChecklistResponse>) {
      state.loanProgress = action.payload;
    },
    toggleCollapsed(state) {
      state.isCollapsed = !state.isCollapsed;
    },
    saveQuestionAnswers(state, action: PayloadAction<QuestionAnswersResponse>) {
      state.questionAnswers = action.payload;
    },
  },
});

export const {
  saveCurrentQuestion,
  saveApplicationProgress,
  saveLoanProgress,
  toggleCollapsed,
  saveQuestionAnswers,
} = ApplicationSlice.actions;

export const fetchApplicationProgress =
  (borrowerId: string) => async (dispatch: AppDispatch) => {
    try {
      const { data } = await new ApplicationApi(
        await getAtlantisConfiguration(),
      ).getProgressSummary(borrowerId);
      dispatch(saveApplicationProgress(data));
    } catch (e: any) {
      Logger.error('Failed to fetch application progress:', e);
      ErrorService.notify('Failed to fetch application progress', e);
      dispatch(showErrorToast('Failed to fetch application progress'));
    }
  };

export const fetchOnboardingApplicationProgress =
  (applicationId: string) => async (dispatch: AppDispatch) => {
    try {
      const { data } = await new sherlockApplicationApi(
        await getSherlockConfiguration(),
      ).getProgressSummary(applicationId!);
      dispatch(saveApplicationProgress(data));
    } catch (e: any) {
      Logger.error('Failed to fetch application progress:', e);
      ErrorService.notify('Failed to fetch application progress', e);
      dispatch(showErrorToast('Failed to fetch application progress'));
    }
  };

export const fetchAgentOnboardingApplicationProgress =
  (applicationId: string, options?: AxiosRequestConfig) =>
  async (dispatch: AppDispatch) => {
    try {
      const { data } = await new ApplicationControllerApi(
        await getYentaConfiguration(),
      ).getProgressSummary(applicationId!, options);
      dispatch(saveApplicationProgress(data));
    } catch (e: any) {
      Logger.error('Failed to fetch application progress:', e);
      ErrorService.notify('Failed to fetch application progress', e);
      dispatch(showErrorToast('Failed to fetch application progress'));
    }
  };

export const fetchLoanProgress =
  (roadToSuccessId: string) => async (dispatch: AppDispatch) => {
    try {
      const { data } = await new ChecklistApi(
        await getSherlockConfiguration(),
      ).getChecklistById(roadToSuccessId);
      dispatch(saveLoanProgress(data));
    } catch (e: any) {
      Logger.error('Failed to fetch loan progress:', e);
      ErrorService.notify('Failed to fetch loan progress', e);
      dispatch(showErrorToast('Failed to fetch loan progress'));
    }
  };

export const fetchApplicationAnswers =
  (applicationId: string) => async (dispatch: AppDispatch) => {
    try {
      const { data } = await new ApplicationControllerApi(
        await getYentaConfiguration(),
      ).getAllVisibleQuestionAnswers(applicationId, {
        requiredQueries: ['all'],
      });
      dispatch(saveQuestionAnswers(data));
      return data;
    } catch (e: any) {
      Logger.error('Failed to fetch application answers:', e);
      ErrorService.notifyIgnoreAuthErrors(
        'Failed to fetch application answers',
        e,
      );
      dispatch(showErrorToast('Failed to fetch application answers'));
      throw e;
    }
  };

export const verifyAgentLicenseNumber =
  (agentId: string, imageToScan: File, licenseNumber: string) =>
  async (dispatch: AppDispatch): Promise<boolean> => {
    try {
      const { data } = await new AgentControllerApi(
        await getYentaConfiguration(),
      ).verifyLicense(agentId, licenseNumber, imageToScan);
      return data?.status === AgentLicenseVerificationResponseStatusEnum.Valid;
    } catch (e: any) {
      dispatch(showErrorToast('Unable to verify the license image'));
      ErrorService.notify('Unable to verify the license image', e, {
        agent: { agentId },
      });
      return false;
    }
  };

export default ApplicationSlice.reducer;
